import { InteractiveContainer } from "@jugl-web/ui-kit";
import { ClickAwayListener, cx, useTranslations } from "@jugl-web/utils";
import { FC, KeyboardEvent, useState } from "react";
import { ReactComponent as ChecklistIcon } from "./assets/checklist.svg";
import { ReactComponent as PenIcon } from "./assets/pen.svg";

export interface EditableDropdownFieldNameProps {
  fieldName: string;
  onChange: (fieldName: string) => void;
}

export const EditableDropdownFieldName: FC<EditableDropdownFieldNameProps> = ({
  fieldName,
  onChange,
}) => {
  const [inputValue, setInputValue] = useState(fieldName);
  const [isEditing, setIsEditing] = useState(false);
  const { t } = useTranslations();

  const handleSubmit = () => {
    if (inputValue !== fieldName) {
      onChange(inputValue);
    }

    setIsEditing(false);
  };

  const handleCancel = () => {
    setIsEditing(false);
  };

  const handleClick = () => {
    setIsEditing(true);
    setInputValue(fieldName);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    event.stopPropagation();

    if (event.key === "Enter") {
      handleSubmit();
    } else if (event.key === "Escape") {
      handleCancel();
    }
  };

  return (
    <ClickAwayListener isDisabled={!isEditing} onClickAway={handleSubmit}>
      <InteractiveContainer
        className={cx(
          "flex h-10 w-[350px] items-center gap-2 rounded-xl px-2 transition-colors hover:bg-grey-200",
          isEditing && "bg-grey-200 outline outline-2 outline-primary-300"
        )}
        onClick={handleClick}
      >
        {isEditing ? (
          <>
            <PenIcon className="shrink-0" />
            <input
              type="text"
              placeholder={t({
                id: "tasks-page.enter-field-name",
                defaultMessage: "Enter field name",
              })}
              className="w-full border-none bg-transparent font-[Roboto] text-base font-medium text-dark outline-none placeholder:font-normal placeholder:text-grey-600"
              autoFocus
              value={inputValue}
              onChange={(event) => setInputValue(event.target.value)}
              onKeyDown={handleKeyDown}
            />
          </>
        ) : (
          <>
            <ChecklistIcon className="shrink-0" />
            <span className="truncate text-base font-medium text-dark">
              {fieldName}
            </span>
          </>
        )}
      </InteractiveContainer>
    </ClickAwayListener>
  );
};
